import Phaser from 'phaser';
import { GameScene } from './GameScene';
import { CampaignState, ProgressStore } from '../state/CampaignState';

export interface BriefingSceneData {
  missionNumber?: number;
  missionName?: string;
  objectives?: string[];
  troopCount?: number;
  difficulty?: string;
}

/** Static levels available for debug-jump. Matches keys registered in PreloadScene. */
const DEBUG_LEVELS = [
  { key: 'level1', label: 'Level 1', missionNumber: 1 },
  { key: 'level2', label: 'Level 2', missionNumber: 2 },
  { key: 'level3', label: 'Level 3', missionNumber: 3 },
  { key: 'level4', label: 'Level 4', missionNumber: 4 },
  { key: 'test-tile-speed', label: 'Test: Tile Speed', missionNumber: 1 },
];

export class BriefingScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BriefingScene' });
  }

  create(data?: BriefingSceneData): void {
    const { width, height } = this.scale;
    const missionNumber = data?.missionNumber ?? 1;
    const missionName = data?.missionName ?? 'Operation Freefall';
    const objectives = data?.objectives ?? [
      'Eliminate all enemies',
      'Destroy enemy HQ',
    ];
    const troopCount = data?.troopCount ?? 4;
    const difficulty = data?.difficulty ?? 'normal';
    const difficultyColor = difficulty === 'easy' ? '#00ff88' : difficulty === 'hard' ? '#ff4444' : '#ffcc00';

    this.cameras.main.fadeIn(600, 0, 0, 0);

    // Background
    this.add.rectangle(0, 0, width, height, 0x0d1b2a).setOrigin(0, 0);

    // Top border stripe
    this.add.rectangle(0, 0, width, 6, 0x00ff88).setOrigin(0, 0);
    this.add.rectangle(0, height - 6, width, 6, 0x00ff88).setOrigin(0, 0);

    // Mission header
    this.add
      .text(width / 2, 50, `MISSION ${missionNumber}`, {
        fontSize: '14px',
        color: '#00ff88',
        fontFamily: 'monospace',
        letterSpacing: 6,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0);

    this.add
      .text(width / 2, 80, missionName, {
        fontSize: '28px',
        color: '#ffffff',
        fontFamily: 'monospace',
        stroke: '#000000',
        strokeThickness: 4,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0);

    // Divider
    this.add.rectangle(width / 2, 130, 400, 1, 0x334455).setOrigin(0.5, 0);

    // Objectives section
    this.add
      .text(80, 150, 'OBJECTIVES', {
        fontSize: '12px',
        color: '#00ff88',
        fontFamily: 'monospace',
        letterSpacing: 3,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0, 0);

    let yOffset = 175;
    for (const obj of objectives) {
      this.add
        .text(80, yOffset, `▸  ${obj}`, {
          fontSize: '16px',
          color: '#cccccc',
          fontFamily: 'monospace',
          resolution: window.devicePixelRatio || 1,
        })
        .setOrigin(0, 0);
      yOffset += 28;
    }

    // Difficulty section
    this.add.rectangle(width / 2, yOffset + 10, 400, 1, 0x334455).setOrigin(0.5, 0);
    this.add
      .text(80, yOffset + 20, 'DIFFICULTY', {
        fontSize: '12px', color: '#00ff88', fontFamily: 'monospace', letterSpacing: 3,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0, 0);
    this.add
      .text(80, yOffset + 48, difficulty.toUpperCase(), {
        fontSize: '16px', color: difficultyColor, fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0, 0);

    // Troops section
    this.add.rectangle(width / 2, yOffset + 78, 400, 1, 0x334455).setOrigin(0.5, 0);
    this.add
      .text(80, yOffset + 88, 'TROOPS AVAILABLE', {
        fontSize: '12px',
        color: '#00ff88',
        fontFamily: 'monospace',
        letterSpacing: 3,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0, 0);

    this.add
      .text(80, yOffset + 116, `${troopCount} soldiers`, {
        fontSize: '16px',
        color: '#cccccc',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0, 0);

    // Start Mission button
    this.createButton(width / 2, height - 90, 'START MISSION', 0x00ff88, 0x004422, () => {
      // Unlock AudioContext on first user gesture
      if ((this.sound as any).context?.state === 'suspended') {
        (this.sound as any).context.resume();
      }
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('GameScene');
      });
    });

    // F5 — debug level select
    this.input.keyboard!.once('keydown-F5', () => {
      this.showLevelSelectOverlay();
    });

    // Hint text
    this.add
      .text(width / 2, height - 40, 'Press ESC to cancel targeting • G/R = switch weapon for selected unit • N = New Map • F5 = Level Select', {
        fontSize: '11px',
        color: '#556677',
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0);

    // ? Help button (bottom-left) — opens standalone help page
    this.createButton(120, height - 55, '? HELP', 0x556677, 0x0d1b2a, () => {
      window.open('pages/help.html', '_blank');
    });

    // New Game button — only shown when there is saved progress to reset
    if (ProgressStore.load() !== null) {
      this.createButton(width - 120, height - 55, 'NEW GAME', 0xff4444, 0x1a0000, () => {
        CampaignState.reset(); // also clears localStorage
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const level1Data: any = (this.cache.tilemap.get('level1') as any)?.data;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const level1Meta: any = level1Data?.metadata;
        this.scene.start('BriefingScene', {
          missionNumber: 1,
          missionName: level1Meta?.name ?? 'Operation Freefall',
          objectives: level1Meta?.objectives?.map((o: { label: string }) => o.label)
            ?? ['Eliminate all enemies', 'Destroy enemy HQ'],
          troopCount: CampaignState.getLiving().length,
          difficulty: level1Data?.difficulty ?? 'normal',
        });
      });
    }

    // Map Editor button (top-left corner)
    this.createButton(120, 30, 'MAP EDITOR', 0x4499ff, 0x001133, () => {
      this.cameras.main.fadeOut(400, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('MapEditorScene');
      });
    });

    // 🎲 New Map debug button (top-right corner)
    this.createButton(width - 120, 30, '🎲 New Map', 0xffcc00, 0x332200, () => {
      GameScene.generateNewLevel(this.game);
      this.cameras.main.fadeOut(400, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.start('GameScene');
      });
    });
  }

  private showLevelSelectOverlay(): void {
    const { width, height } = this.scale;
    const panelW = 320;
    const panelH = 60 + DEBUG_LEVELS.length * 52 + 20;
    const panelX = (width - panelW) / 2;
    const panelY = (height - panelH) / 2;

    // Dim backdrop
    const backdrop = this.add
      .rectangle(0, 0, width, height, 0x000000, 0.7)
      .setOrigin(0, 0)
      .setDepth(100)
      .setInteractive();

    // Panel background
    const panel = this.add
      .rectangle(panelX, panelY, panelW, panelH, 0x0d1b2a)
      .setOrigin(0, 0)
      .setDepth(101)
      .setStrokeStyle(2, 0x00ff88);

    const title = this.add
      .text(width / 2, panelY + 20, 'SELECT LEVEL', {
        fontSize: '14px',
        color: '#00ff88',
        fontFamily: 'monospace',
        letterSpacing: 4,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0)
      .setDepth(102);

    const overlayObjects: Phaser.GameObjects.GameObject[] = [backdrop, panel, title];

    const closeOverlay = () => {
      overlayObjects.forEach((o) => o.destroy());
      // Re-register F5 so it can be used again
      this.input.keyboard!.once('keydown-F5', () => this.showLevelSelectOverlay());
    };

    backdrop.on('pointerdown', closeOverlay);

    DEBUG_LEVELS.forEach((lvl, i) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const meta: any = (this.cache.tilemap.get(lvl.key) as any)?.data?.metadata;
      const label = meta?.name ? `${lvl.label}: ${meta.name}` : lvl.label;
      const difficulty: string = meta?.difficulty ?? 'normal';
      const diffColor = difficulty === 'easy' ? '#00ff88' : difficulty === 'hard' ? '#ff4444' : '#ffcc00';

      const btnY = panelY + 56 + i * 52;
      const btnX = width / 2;

      const bg = this.add
        .rectangle(btnX, btnY + 18, panelW - 24, 42, 0x112233)
        .setOrigin(0.5, 0)
        .setDepth(102)
        .setStrokeStyle(1, 0x334455)
        .setInteractive({ useHandCursor: true });

      const labelText = this.add
        .text(btnX - (panelW / 2 - 24), btnY + 22, label, {
          fontSize: '14px',
          color: '#ffffff',
          fontFamily: 'monospace',
          resolution: window.devicePixelRatio || 1,
        })
        .setOrigin(0, 0)
        .setDepth(103);

      const diffText = this.add
        .text(btnX + (panelW / 2 - 24), btnY + 22, difficulty.toUpperCase(), {
          fontSize: '12px',
          color: diffColor,
          fontFamily: 'monospace',
          resolution: window.devicePixelRatio || 1,
        })
        .setOrigin(1, 0)
        .setDepth(103);

      bg.on('pointerover', () => bg.setFillStyle(0x223344));
      bg.on('pointerout', () => bg.setFillStyle(0x112233));
      bg.on('pointerdown', () => {
        overlayObjects.forEach((o) => o.destroy());
        [bg, labelText, diffText].forEach((o) => o.destroy());
        this.jumpToLevel(lvl.key, lvl.missionNumber);
      });

      overlayObjects.push(bg, labelText, diffText);
    });

    // ESC to close
    this.input.keyboard!.once('keydown-ESC', closeOverlay);
  }

  private jumpToLevel(levelKey: string, missionNumber: number): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const levelData: any = (this.cache.tilemap.get(levelKey) as any)?.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const meta: any = levelData?.metadata;

    CampaignState.missionNumber = missionNumber;
    GameScene.useGeneratedLevel = false;

    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('BriefingScene', {
        missionNumber,
        missionName: meta?.name ?? `Level ${missionNumber}`,
        objectives: meta?.objectives?.map((o: { label: string }) => o.label) ?? ['Eliminate all enemies'],
        troopCount: CampaignState.getLiving().length,
        difficulty: levelData?.difficulty ?? meta?.difficulty ?? 'normal',
      });
    });
  }

  private createButton(
    x: number,
    y: number,
    label: string,
    textColor: number,
    bgColor: number,
    onClick: () => void
  ): void {
    const btnWidth = 220;
    const btnHeight = 44;

    const bg = this.add
      .rectangle(x, y, btnWidth, btnHeight, bgColor)
      .setStrokeStyle(2, textColor)
      .setInteractive({ useHandCursor: true });

    const text = this.add
      .text(x, y, label, {
        fontSize: '16px',
        color: `#${textColor.toString(16).padStart(6, '0')}`,
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    bg.on('pointerover', () => {
      bg.setFillStyle(textColor);
      text.setColor(`#${bgColor.toString(16).padStart(6, '0')}`);
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(bgColor);
      text.setColor(`#${textColor.toString(16).padStart(6, '0')}`);
    });
    bg.on('pointerdown', onClick);
  }
}
